import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  Stack,
  Chip,
  Fab,
  Popover,
  Divider,
  Grid,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../../constants/icons';
import { useMasterData } from '../../providers/MasterDataProvider';
import { STANDARD_FAB_CTA_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { generateFiscalYearOptions } from '../profitAndLoss/constants';
import type { UseMonthlyTrialBalanceResult } from '../../hooks/useMonthlyTrialBalance';

type Props = {
  tb: UseMonthlyTrialBalanceResult;
};

/**
 * 月次合計試算表ページの見出し＋絞り込みヘッダー。
 * 現在の絞り込み条件（年度・拠点・部署）を Chip で要約表示し、Fab から検索 Popover を開く。
 * Popover 内では draft を編集し、［検索を実行］押下時にまとめてフックへ反映する（即時フェッチしない）。
 */
export default function MonthlyTrialBalanceFilterHeader({ tb }: Props) {
  const { branchList, departmentList } = useMasterData();
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);

  // draft (ローカル) state — Popover 内で編集し実行時にまとめて反映
  const [localFiscalYear, setLocalFiscalYear] = useState<number>(tb.fiscalYear);
  const [localBranchCodes, setLocalBranchCodes] = useState<string[]>(tb.selectedBranchCodes);
  const [localDepartmentCodes, setLocalDepartmentCodes] = useState<string[]>(tb.selectedDepartmentCodes);

  useEffect(() => { setLocalFiscalYear(tb.fiscalYear); }, [tb.fiscalYear]);
  useEffect(() => { setLocalBranchCodes(tb.selectedBranchCodes); }, [tb.selectedBranchCodes]);
  useEffect(() => { setLocalDepartmentCodes(tb.selectedDepartmentCodes); }, [tb.selectedDepartmentCodes]);

  const handleApply = () => {
    tb.setFiscalYear(localFiscalYear);
    tb.setSelectedBranchCodes(localBranchCodes);
    tb.setSelectedDepartmentCodes(localDepartmentCodes);
    setFilterAnchorEl(null);
  };

  const handleClear = () => {
    setLocalBranchCodes([]);
    setLocalDepartmentCodes([]);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
          <Icons.ReceiptLong /> 月次合計試算表 <span style={{ fontSize: '18px', fontWeight: 500, color: '#64748b' }}>- {tb.fiscalYear}年度 通期</span>
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569', mb: 0 }}>
          {tb.fiscalYear}年度（9〜8月）の勘定科目別 月次推移（勘定科目をクリックで補助科目内訳を展開）
        </Typography>
      </Box>

      {/* 絞り込み条件サマリー + 検索フィルター起動ボタン */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.5 }}>
          <Chip size="small" label={`${tb.fiscalYear}年度`} sx={{ fontSize: '11px', height: '22px', bgcolor: '#eff6ff', color: '#1e3a8a', fontWeight: 600 }} />
          <Chip size="small" label={tb.selectedBranchCodes.length === 0 ? '全拠点' : `拠点 ${tb.selectedBranchCodes.length}件`} sx={{ fontSize: '11px', height: '22px' }} />
          <Chip size="small" label={tb.selectedDepartmentCodes.length === 0 ? '全部署' : `部署 ${tb.selectedDepartmentCodes.length}件`} sx={{ fontSize: '11px', height: '22px' }} />
        </Stack>
        <Fab
          variant="extended"
          size="small"
          onClick={(e) => setFilterAnchorEl(e.currentTarget)}
          sx={STANDARD_FAB_CTA_SX}
        >
          <Icons.FilterAltOutlined />
          絞り込み
        </Fab>
      </Box>

      {/* 🔍 検索フィルター Popover (会計年度・拠点・部署を指定して実行) */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { mt: 1, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', borderRadius: 2, overflow: 'hidden', border: '1px solid #edf2f7' },
        }}
      >
        <Card sx={{ width: '520px', border: 'none', boxShadow: 'none' }}>
          <Box sx={{ p: 1, borderBottom: '1px solid #eee', bgcolor: '#f1f5f9' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#334155', fontSize: '13px' }}>
              合計試算表を絞り込み
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {/* 会計年度 */}
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: '13px' }}>会計年度</InputLabel>
                  <Select
                    label="会計年度"
                    value={localFiscalYear}
                    onChange={(e) => setLocalFiscalYear(Number(e.target.value))}
                    sx={{ fontSize: '13px', backgroundColor: 'background.paper' }}
                  >
                    {generateFiscalYearOptions().map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '13px' }}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* 拠点 (複数選択) */}
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={branchList}
                  getOptionLabel={(opt: any) => opt.branch_name || ''}
                  isOptionEqualToValue={(opt: any, val: any) => opt.branch_code === val.branch_code}
                  value={branchList.filter((b: any) => localBranchCodes.includes(b.branch_code))}
                  onChange={(_e, newValue) => setLocalBranchCodes(newValue.map((b: any) => b.branch_code))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="拠点"
                      placeholder={localBranchCodes.length === 0 ? '拠点で絞り込み (複数選択可能)' : ''}
                      sx={{ '& .MuiInputLabel-root': { fontSize: '13px' }, '& .MuiOutlinedInput-input': { fontSize: '13px' }, '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option: any, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip key={key} label={option.branch_name} {...tagProps} size="small" sx={{ fontSize: '11px', height: '20px' }} />;
                    })
                  }
                />
              </Grid>

              {/* 部署 (複数選択) */}
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={departmentList}
                  getOptionLabel={(opt: any) => opt.department_name || ''}
                  isOptionEqualToValue={(opt: any, val: any) => opt.department_code === val.department_code}
                  value={departmentList.filter((d: any) => localDepartmentCodes.includes(d.department_code))}
                  onChange={(_e, newValue) => setLocalDepartmentCodes(newValue.map((d: any) => d.department_code))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="部署"
                      placeholder={localDepartmentCodes.length === 0 ? '部署で絞り込み (複数選択可能)' : ''}
                      sx={{ '& .MuiInputLabel-root': { fontSize: '13px' }, '& .MuiOutlinedInput-input': { fontSize: '13px' }, '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option: any, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip key={key} label={option.department_name} {...tagProps} size="small" sx={{ fontSize: '11px', height: '20px' }} />;
                    })
                  }
                />
              </Grid>
            </Grid>
          </Box>

          {/* アクションボタン */}
          <Divider sx={{ flexShrink: 0 }} />
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f1f5f9', flexShrink: 0 }}>
            <Button size="small" onClick={handleClear} startIcon={<Icons.RestartAlt />} sx={STANDARD_TEXT_BUTTON_SX}>
              クリア
            </Button>
            <Fab variant="extended" size="small" disableRipple onClick={handleApply} sx={STANDARD_FAB_CTA_SX}>
              <Icons.Search />
              検索を実行
            </Fab>
          </Box>
        </Card>
      </Popover>
    </Box>
  );
}
